import React, { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  author?: string;
  type?: 'website' | 'article' | 'profile';
  noIndex?: boolean;
  canonical?: string;
}

const SEO: React.FC<SEOProps> = ({
  title = 'YouMeditation - Guided & Unguided Meditation Scripts',
  description = 'Create, share, and discover personalized meditation experiences. Generate custom meditation scripts with AI, practice guided meditations, or enjoy peaceful unguided sessions.',
  keywords = 'meditation, mindfulness, guided meditation, unguided meditation, meditation scripts, AI meditation, relaxation, wellness, mental health',
  image = '/default-meditation-image.jpg',
  url,
  author,
  type = 'website',
  noIndex = false,
  canonical
}) => {
  useEffect(() => {
    // Update document title
    document.title = title;

    // Construct full URLs
    const fullUrl = url ? `${window.location.origin}${url}` : window.location.href;
    const fullImageUrl = image?.startsWith('http') ? image : `${window.location.origin}${image}`;

    // Helper function to update or create meta tags
    const updateMetaTag = (name: string, content: string, property = false) => {
      const attribute = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
      
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // Helper function to update or create link tags
    const updateLinkTag = (rel: string, href: string) => {
      let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      
      if (!link) {
        link = document.createElement('link');
        link.rel = rel;
        document.head.appendChild(link);
      }
      link.href = href;
    };

    // Update basic meta tags
    updateMetaTag('description', description);
    if (keywords) updateMetaTag('keywords', keywords);
    if (author) updateMetaTag('author', author);
    
    // Update robots tag
    if (noIndex) {
      updateMetaTag('robots', 'noindex, nofollow');
    } else {
      // Remove noindex if it exists
      const robotsMeta = document.querySelector('meta[name="robots"]');
      if (robotsMeta && robotsMeta.getAttribute('content') === 'noindex, nofollow') {
        robotsMeta.remove();
      }
    }

    // Update Open Graph tags
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:url', fullUrl, true);
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:image', fullImageUrl, true);
    updateMetaTag('og:site_name', 'YouMeditation', true);
    updateMetaTag('fb:app_id', '771085842284813', true);

    // Update Twitter tags
    updateMetaTag('twitter:card', 'summary_large_image', true);
    updateMetaTag('twitter:url', fullUrl, true);
    updateMetaTag('twitter:title', title, true);
    updateMetaTag('twitter:description', description, true);
    updateMetaTag('twitter:image', fullImageUrl, true);

    // Update additional meta tags
    updateMetaTag('theme-color', '#667eea');
    updateMetaTag('application-name', 'YouMeditation');

    // Update canonical link
    if (canonical) {
      updateLinkTag('canonical', canonical);
    }

    // Cleanup function to reset title on unmount (optional)
    return () => {
      // We don't reset meta tags on unmount as they should persist
      // until the next page sets new ones
    };
  }, [title, description, keywords, image, url, author, type, noIndex, canonical]);

  // This component doesn't render anything visible
  return null;
};

export default SEO;